import { router } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { GoalIconPicker } from '@/components/goal-icon-picker';
import { AppText } from '@/components/ui/app-text';
import { DateField } from '@/components/ui/date-field';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { useGoalsStore } from '@/store/goals-store';
import { colors, radius, spacing } from '@/theme/tokens';
import { daysUntil, formatDate, formatPln } from '@/utils/format';
import type { SavingsGoal } from '@/types/domain';

const TEMPLATES: { key: SavingsGoal['template']; labelKey: string }[] = [
  { key: 'DOWN_PAYMENT', labelKey: 'newGoal.templateDownPayment' },
  { key: 'VACATION', labelKey: 'newGoal.templateVacation' },
  { key: 'EMERGENCY_FUND', labelKey: 'newGoal.templateEmergencyFund' },
  { key: 'CUSTOM', labelKey: 'newGoal.templateCustom' },
];

/** "45000" / "45 000" -> grosze (integer złoty input) */
function parseZl(raw: string): number {
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) * 100 : 0;
}

/** Mock: nowy_cel/screen.png — "Ile już macie odłożone?" so the ring never starts at 0% */
export default function NewGoalScreen() {
  const { t } = useTranslation();
  const addGoal = useGoalsStore((s) => s.addGoal);

  const [name, setName] = useState('');
  const [template, setTemplate] = useState<SavingsGoal['template']>('CUSTOM');
  const [targetRaw, setTargetRaw] = useState('');
  const [startingRaw, setStartingRaw] = useState('');
  const [targetDate, setTargetDate] = useState<string | null>(null); // ISO
  const [icon, setIcon] = useState<string | null>(null);

  const targetGr = parseZl(targetRaw);
  const startingGr = parseZl(startingRaw);
  const valid = name.trim().length > 0 && targetGr > 0 && startingGr < targetGr;

  // Forecast INVERTED (user request): given a target date, tell them how much to
  // put aside each month to make it — not "at 500/mo it takes 25 years".
  const daysToTarget = targetDate ? daysUntil(targetDate) : null;
  let forecast: string;
  if (!targetDate) {
    forecast = t('newGoal.forecastNoDate');
  } else if (daysToTarget !== null && daysToTarget <= 0) {
    forecast = t('newGoal.forecastPast');
  } else {
    const months = Math.max(1, Math.round((daysToTarget as number) / 30.44));
    const requiredMonthlyGr = Math.ceil(Math.max(0, targetGr - startingGr) / months);
    forecast = t('newGoal.forecastNeeded', {
      date: formatDate(targetDate),
      months,
      amount: formatPln(requiredMonthlyGr),
    });
  }

  const selectTemplate = (key: SavingsGoal['template'], label: string) => {
    setTemplate(key);
    if (!name.trim() && key !== 'CUSTOM') setName(label);
  };

  const onCreate = async () => {
    await addGoal({ name: name.trim(), template, targetGr, startingGr, targetDate, icon });
    router.back();
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
      >
        <ScreenHeader title={t('newGoal.title')} />

        {/* Motto banner */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: spacing.sm,
            backgroundColor: colors.primaryTile,
            borderRadius: radius.card,
            padding: spacing.md,
          }}
        >
          <Ionicons name="flag-outline" size={22} color={colors.navy} />
          <AppText variant="labelLg" color={colors.navy}>
            {t('newGoal.motto')}
          </AppText>
        </View>

        <TextField
          label={t('newGoal.nameLabel')}
          placeholder={t('newGoal.namePlaceholder')}
          value={name}
          onChangeText={setName}
        />

        {/* Template chips */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs }}>
          {TEMPLATES.map(({ key, labelKey }) => {
            const active = template === key;
            return (
              <Pressable
                key={key}
                accessibilityRole="button"
                onPress={() => selectTemplate(key, t(labelKey))}
                style={{
                  backgroundColor: active ? colors.navy : colors.card,
                  borderRadius: radius.full,
                  paddingHorizontal: spacing.md,
                  paddingVertical: spacing.xs,
                }}
              >
                <AppText variant="labelLg" color={active ? '#FFFFFF' : colors.navy}>
                  {t(labelKey)}
                </AppText>
              </Pressable>
            );
          })}
        </View>

        <TextField
          label={t('newGoal.targetLabel')}
          placeholder="np. 8 000"
          keyboardType="numeric"
          value={targetRaw}
          onChangeText={setTargetRaw}
        />

        {/* "How much do you already have?" — the never-start-at-zero rule */}
        <View style={{ backgroundColor: colors.primaryTile, borderRadius: radius.card, padding: spacing.md, gap: spacing.sm }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
            <Ionicons name="sparkles-outline" size={18} color={colors.navy} />
            <AppText variant="labelLg" color={colors.navy}>
              {t('newGoal.startingLabel')}
            </AppText>
          </View>
          <TextField placeholder="np. 1 500" keyboardType="numeric" value={startingRaw} onChangeText={setStartingRaw} />
          <AppText variant="bodyMd" color={colors.navy}>
            {t('newGoal.startingHint')}
          </AppText>
        </View>

        <DateField
          label={t('newGoal.dateLabel')}
          value={targetDate}
          onChange={setTargetDate}
          minIso={new Date().toISOString().slice(0, 10)}
        />

        {/* Icon grid (mock: edycja_celu). Falls back by template when untouched. */}
        <AppText variant="labelLg" color={colors.navy}>
          {t('editGoal.iconLabel')}
        </AppText>
        <GoalIconPicker value={icon} onChange={setIcon} />

        {/* Forecast */}
        {targetGr > 0 && (
          <View style={{ flexDirection: 'row', gap: spacing.sm, backgroundColor: colors.card, borderRadius: radius.card, padding: spacing.md }}>
            <IconBubble />
            <View style={{ flex: 1, gap: 2 }}>
              <AppText variant="labelLg">{t('newGoal.forecastTitle')}</AppText>
              <AppText variant="bodyMd">{forecast}</AppText>
            </View>
          </View>
        )}

        <PrimaryButton
          label={t('newGoal.create')}
          disabled={!valid}
          onPress={onCreate}
          style={{ backgroundColor: valid ? colors.navy : undefined }}
        />

        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs }}>
          <Ionicons name="lock-closed-outline" size={14} color={colors.textSecondary} />
          <AppText variant="labelMd" style={{ textAlign: 'center' }}>
            {t('newGoal.dataSafe')}
          </AppText>
        </View>
      </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

function IconBubble() {
  return (
    <View
      style={{
        width: 40,
        height: 40,
        borderRadius: radius.full,
        backgroundColor: colors.infoBg,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Ionicons name="trending-up-outline" size={20} color={colors.primary} />
    </View>
  );
}
